import React from 'react';
import {
    Box, Grid, TextField, Paper, TableBody, TableCell,
    Button, TableContainer, Table, Typography,
    FormControl, InputLabel, MenuItem, TableHead, TableRow,
    IconButton, Tabs, Tab, Stack, Chip, Divider, Select,
    SelectChangeEvent, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import {
    Save, Delete, Create, Timeline, SearchOutlined, Add,
} from '@mui/icons-material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { customAlphabet } from 'nanoid';

import toast from 'react-hot-toast';
import { ConfirmDialogContext } from '../common/ConfirmDialogProvider';
import { addInvoice, getInvoice } from '../Service/invoice.service';
import DataGrid from 'components/table/DataGrid';

interface SaleItem {
    item: string;
    shippedQuantity: string;
    unitPrice: string;
    totalPrice: number;
    id: number;
}

interface IRecord {
    _id: string;
    id?: string;
    driverName: string;
    invoiceNo: number;
    invoiceDate: Date;
    items: SaleItem[];
    status: string;
    createdAt: string;
    updatedAt: string;
}

const InvoiceManagement: React.FC = () => {
    const numbers = '012345678';
    const nanoid = customAlphabet(numbers, 10);
    const nanoid1 = customAlphabet(numbers, 4);

    const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(dayjs(new Date()));
    const [recordData, setRecordData] = React.useState<any>({});
    const [invoices, setInvoices] = React.useState<IRecord[]>([]);
    const [showSave, setShowSave] = React.useState(false);
    const [currentSaleItems, setCurrentSaleItems] = React.useState<SaleItem[]>([]);
    const [value, setValue] = React.useState(0);

    const [sale, setSale] = React.useState({
        driverName: '',
        item: '',
        shippedQuantity: 0,
        unitPrice: 0,
    });

    const [requestData, setRequestData] = React.useState({
        requestType: 'invoice',
        invoiceType: 'release',
        invoiceNo: '',
        startDate: null,
        endDate: null,
    });

    const handleChange = (
        event: SelectChangeEvent<any> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = event.target;
        setSale({ ...sale, [name as string]: value });
    };

    const handleDateChange = (date: Dayjs | null) => setSelectedDate(date);

    const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        setInvoices([]);
        setRecordData({});
    };

    const itemTotal = () => Number(sale.shippedQuantity) * sale.unitPrice;

    const addItemToSale = () => {
        if (!sale.item || !sale.shippedQuantity || !sale.unitPrice) {
            toast('Please fill in all item fields correctly', { icon: '⚠️' });
            return;
        }
        if (currentSaleItems.some((item) => item.item === sale.item)) {
            toast('This item has already been added', { icon: '⚠️' });
            return;
        }
        const newItem: SaleItem = {
            id: parseInt(nanoid1(), 10),
            item: sale.item,
            shippedQuantity: String(sale.shippedQuantity),
            unitPrice: String(sale.unitPrice),
            totalPrice: itemTotal(),
        };
        setCurrentSaleItems((prev) => [...prev, newItem]);
    };

    const finalizeSaleFunction = () => {
        if (!sale.driverName || !selectedDate || currentSaleItems.length === 0) {
            toast('Please fill in all fields correctly', { icon: '⚠️' });
            return;
        }
        const newSaleRecord = {
            driverName: sale.driverName,
            invoiceDate: selectedDate ? selectedDate.toDate() : null,
            items: currentSaleItems,
            status: 'release',
            invoiceNo: nanoid(),
        };
        setRecordData(newSaleRecord);
        setShowSave(true);
        toast.success('Saved locally — ready to submit to database');
    };

    const removeRowData = (idToRemove: number) => {
        setCurrentSaleItems((prev) => prev.filter((item) => item.id !== idToRemove));
        toast.error('Row removed');
    };

    const handleRequestTypeChange = (event: SelectChangeEvent<string>) => {
        const { value } = event.target;
        setRequestData((prev) => ({
            ...prev,
            requestType: value,
            invoiceType: value === 'status' ? 'release' : 'open',
            startDate: null,
            endDate: null,
        }));
    };

    const handleInvoiceTypeChange = (event: SelectChangeEvent<string>) => {
        setRequestData((prev) => ({ ...prev, invoiceType: event.target.value }));
    };

    const handleInvoiceNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRequestData((prev) => ({ ...prev, invoiceNo: event.target.value }));
    };

    const handleStartDateChange = (date: any) => {
        setRequestData((prev) => ({ ...prev, startDate: date }));
    };

    const handleEndDateChange = (date: any) => {
        setRequestData((prev) => ({ ...prev, endDate: date }));
    };

    const handleInvoice = async () => {
        try {
            showConfirmDialog('Save Invoice', 'Do you want to save this invoice to the database?', async () => {
                const result = await addInvoice(recordData);
                if (result) {
                    toast.success(result.message);
                    setCurrentSaleItems([]);
                    setRecordData({});
                    setShowSave(false);
                }
            });
        } catch {
            toast.error('An error occurred');
        }
    };

    const invoiceHistory = async () => {
        const { requestType, invoiceType, invoiceNo, startDate, endDate } = requestData;
        let payload = {};
        try {
            if (requestType === 'invoice') {
                payload = { findkey: 'invoiceNo', invoiceNo };
            } else if (requestType === 'status') {
                payload = { findkey: 'status', status: invoiceType };
            } else if (requestType === 'dateRange') {
                payload = { findkey: 'dateRange', startDateISO: startDate, endDateISO: endDate };
            }
            const result = await getInvoice(payload);
            if (result.length === 0) {
                toast('No invoice history found', { icon: '⚠️' });
            } else {
                toast.success('Fetched invoice history successfully');
                setInvoices(result);
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'No record found');
        }
    };

    const columns = [
        { title: 'Driver Name', field: 'driverName' },
        { title: 'Status', field: 'status' },
        { title: 'Invoice No', field: 'invoiceNo' },
        { title: 'Date', field: 'invoiceDate', type: 'date', dateSetting: { locale: 'ko-KR' } },
        { title: 'Created At', field: 'createdAt', type: 'date', dateSetting: { locale: 'ko-KR' } },
    ];

    const renderDetailPanel = ({ row }: { row: any }) => {
        if (!row.original.items?.length) return null;
        return (
            <Box sx={{ m: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1} color="primary.main">
                    Items in this Invoice
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                            <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Shipped Qty</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Total Price</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {row.original.items.map((item: any) => (
                            <TableRow key={item.item}>
                                <TableCell>{item.item}</TableCell>
                                <TableCell>{item.shippedQuantity}</TableCell>
                                <TableCell>{item.unitPrice}</TableCell>
                                <TableCell>{item.totalPrice}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        );
    };

    const grandTotal = currentSaleItems.reduce((sum, item) => sum + item.totalPrice, 0);

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 60%, ${theme.palette.secondary.light} 100%)`,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderRadius: 1,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
                }}
            >
                <Box
                    sx={{
                        bgcolor: alpha('#fff', 0.15),
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ReceiptLongIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
                </Box>
                <Box>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: '#fff', lineHeight: 1.2 }}
                    >
                        Invoice Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                        Create and track delivery invoices
                    </Typography>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2 }}>
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                        overflow: 'hidden',
                    }}
                >
                    {/* Tabs */}
                    <Box
                        sx={{
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            px: { xs: 1, sm: 2 },
                            pt: 1,
                            bgcolor: alpha(theme.palette.secondary.main, 0.03),
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleChangeTab}
                            variant={isMobile ? 'fullWidth' : 'standard'}
                            TabIndicatorProps={{
                                sx: {
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                    bgcolor: 'secondary.main',
                                },
                            }}
                            sx={{
                                minHeight: 52,
                                '& .MuiTab-root': {
                                    minHeight: 52,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    color: 'text.secondary',
                                    gap: 0.75,
                                    '&.Mui-selected': { color: 'secondary.main' },
                                },
                            }}
                        >
                            <Tab label="Create Invoice" icon={<Create fontSize="small" />} iconPosition="start" />
                            <Tab label="Invoice History" icon={<Timeline fontSize="small" />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: 3, pt: 3 }}>
                        {/* ── CREATE INVOICE TAB ── */}
                        {value === 0 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 2.5, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                                >
                                    Invoice Details
                                </Typography>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        p: { xs: 1.5, sm: 2.5 },
                                        mb: 3,
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="flex-end">
                                        {/* Driver Name */}
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Driver Name
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Enter driver name"
                                                value={sale.driverName}
                                                onChange={handleChange}
                                                name="driverName"
                                            />
                                        </Grid>

                                        {/* Date */}
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Invoice Date
                                            </Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    value={selectedDate}
                                                    onChange={handleDateChange}
                                                    slotProps={{
                                                        textField: { size: 'small', fullWidth: true },
                                                        openPickerIcon: { sx: { color: 'secondary.main' } },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Grid>

                                        {/* Item */}
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Item Description
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={sale.item}
                                                    onChange={handleChange}
                                                    name="item"
                                                    displayEmpty
                                                    renderValue={(v) => v || <span style={{ color: '#aaa' }}>Select item</span>}
                                                >
                                                    <MenuItem value="14.2_KG_REFILL">14.2 KG REFILL</MenuItem>
                                                    <MenuItem value="14.2_KG_NC">14.2 KG NC</MenuItem>
                                                    <MenuItem value="14.2_KG_DBC">14.2 KG DBC</MenuItem>
                                                    <MenuItem value="19_KG_REFILL">19 KG REFILL</MenuItem>
                                                    <MenuItem value="19_KG_NC">19 KG NC</MenuItem>
                                                    <MenuItem value="5KG_FTL_REFILL">5KG FTL REFILL</MenuItem>
                                                    <MenuItem value="5KG_FTL_NC">5KG FTL NC</MenuItem>
                                                    <MenuItem value="5KG_DOMESTIC">5KG DOMESTIC</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {/* Qty */}
                                        <Grid item xs={6} sm={3} md={2}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Shipped Qty
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={sale.shippedQuantity}
                                                name="shippedQuantity"
                                                onChange={handleChange}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Unit Price */}
                                        <Grid item xs={6} sm={3} md={2}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Unit Price
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={sale.unitPrice}
                                                name="unitPrice"
                                                onChange={handleChange}
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>

                                        {/* Total */}
                                        <Grid item xs={6} sm={3} md={1}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Total
                                            </Typography>
                                            <Box
                                                sx={{
                                                    height: 40,
                                                    px: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                                                    borderRadius: 1,
                                                    border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={700} color="secondary.dark">
                                                    ₹{itemTotal()}
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        {/* Add Button */}
                                        <Grid item xs={6} sm={3} md={1}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                startIcon={<Add />}
                                                onClick={addItemToSale}
                                                fullWidth
                                                sx={{
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    height: 40,
                                                    textTransform: 'none',
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {/* Items Table */}
                                {currentSaleItems.length > 0 && (
                                    <Box>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                                {currentSaleItems.length} Item{currentSaleItems.length > 1 ? 's' : ''} Added
                                            </Typography>
                                            <Chip
                                                label={`Grand Total: ₹${grandTotal}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                    color: 'secondary.dark',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </Stack>

                                        <TableContainer
                                            component={Paper}
                                            elevation={0}
                                            sx={{
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 2,
                                                mb: 2,
                                                overflow: 'auto',
                                            }}
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Quantity</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total</TableCell>
                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>Remove</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {currentSaleItems.map((record) => (
                                                        <TableRow
                                                            key={record.id}
                                                            sx={{
                                                                '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[500], 0.03) },
                                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={600}>
                                                                    {record.item.replace(/_/g, ' ')}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">{record.shippedQuantity}</TableCell>
                                                            <TableCell align="right">₹{record.unitPrice}</TableCell>
                                                            <TableCell align="right">
                                                                <Typography variant="body2" fontWeight={700} color="secondary.dark">
                                                                    ₹{record.totalPrice}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => removeRowData(record.id)}
                                                                    sx={{
                                                                        color: 'error.main',
                                                                        '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                                                                    }}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    {recordData.invoiceNo && (
                                                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                                            <TableCell colSpan={2}>
                                                                <Typography variant="caption" color="primary.main" fontWeight={600}>
                                                                    Date: {new Date(recordData.invoiceDate).toLocaleDateString()}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell colSpan={1}>
                                                                <Stack direction="row" alignItems="center" gap={0.5}>
                                                                    <LocalShippingOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                    <Typography variant="caption">{recordData.driverName}</Typography>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell colSpan={2}>
                                                                <Chip
                                                                    label={`Invoice #${recordData.invoiceNo}`}
                                                                    size="small"
                                                                    color="primary"
                                                                    variant="outlined"
                                                                    sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Divider sx={{ my: 2 }} />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<Save />}
                                                onClick={finalizeSaleFunction}
                                                sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                                            >
                                                Save Locally
                                            </Button>
                                            {showSave && (
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    startIcon={<Save />}
                                                    onClick={handleInvoice}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                                                    }}
                                                >
                                                    Save to Database
                                                </Button>
                                            )}
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* ── INVOICE HISTORY TAB ── */}
                        {value === 1 && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                    sx={{ mb: 2.5, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}
                                >
                                    Search Invoices
                                </Typography>

                                <Paper
                                    elevation={0}
                                    sx={{
                                        border: `1px solid ${theme.palette.divider}`,
                                        borderRadius: 2,
                                        p: { xs: 1.5, sm: 2.5 },
                                        mb: 3,
                                    }}
                                >
                                    <Grid container spacing={2} alignItems="flex-end">
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Search By
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={requestData.requestType}
                                                    onChange={handleRequestTypeChange}
                                                    name="requestType"
                                                >
                                                    <MenuItem value="invoice">Invoice No</MenuItem>
                                                    <MenuItem value="status">Invoice Status</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {requestData.requestType === 'invoice' && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                    Invoice No
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Enter invoice number"
                                                    value={requestData.invoiceNo}
                                                    onChange={handleInvoiceNoChange}
                                                    name="invoiceNo"
                                                />
                                            </Grid>
                                        )}

                                        {requestData.requestType === 'status' && (
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                    Invoice Status
                                                </Typography>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={requestData.invoiceType}
                                                        onChange={handleInvoiceTypeChange}
                                                        name="invoiceType"
                                                    >
                                                        <MenuItem value="release">Release</MenuItem>
                                                        <MenuItem value="accept">Accept</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        )}

                                        <Grid item xs={12} sm={6} md={3}>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                startIcon={<SearchOutlined />}
                                                onClick={invoiceHistory}
                                                sx={{
                                                    borderRadius: 2,
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    height: 40,
                                                    boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                                                }}
                                            >
                                                Search
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                {invoices.length > 0 && (
                                    <Box
                                        sx={{
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: `1px solid ${theme.palette.divider}`,
                                            '& .MuiPaper-root': { boxShadow: 'none !important' },
                                        }}
                                    >
                                        <DataGrid
                                            title="Invoice History"
                                            rows={invoices}
                                            columns={columns}
                                            renderDetailPanel={renderDetailPanel}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default InvoiceManagement;
